import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { BusinessInput, CategoryInput } from '@barboxx/admin-components';
import {
    CREATE_BUSINESS_CATEGORY_SCHEMA,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessCategory } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateBusinessCategoryDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notifications = useNotifications();
    const { businessId } = useBusinessSelector();

    const formContext = useForm<BusinessCategory.CreateRequest>({
        resolver: yupResolver(CREATE_BUSINESS_CATEGORY_SCHEMA),
        defaultValues: {
            isActive: true,
            businessId,
            featuredCategory: false,
            displayOrder: null,
        },
    });

    const mutateCreateBusinessCategory = useMutation({
        mutationKey: ['BusinessCategory'],
        mutationFn: (data: BusinessCategory.CreateRequest) =>
            Api.BusinessSpecific.Category.postCategory(data),
    });

    const onSubmit = async (values: BusinessCategory.CreateRequest) => {
        return mutateCreateBusinessCategory
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created Business Category!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating business Item!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Business Category
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <BusinessInput />
                    <CategoryInput />
                    <TextFieldElement
                        label="Display Name"
                        name="displayName"
                        fullWidth
                    />
                    <TextFieldElement
                        label="Display Order"
                        name="displayOrder"
                        type="number"
                        fullWidth
                        slotProps={{
                            htmlInput: {
                                max: 1000,
                                min: 0,
                                step: 1,
                            },
                        }}
                    />
                    <CheckboxElement label="Active" name="isActive" />
                    <CheckboxElement label="Featured" name="featuredCategory" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
